/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Button, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { rolesAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { useNotify } from '../../components/common/NotificationProvider'
import { summarize } from '../../components/roles/roleModel'
import { entityLabelPlural } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'

const RolesPage = () => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const notify = useNotify()
  const queryClient = useQueryClient()
  const [deleting, setDeleting] = useState(null)
  const { data: roles, isLoading, error } = useQuery({ queryKey: [tenantId, 'roles'], queryFn: rolesAPI.list })
  const remove = useMutation({
    mutationFn: (id) => rolesAPI.remove(id),
    onSuccess: () => {
      setDeleting(null)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'roles'] })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'members'] })
      notify('Role deleted; its members now have the default role')
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>

  return (
    <>
      <PageHeader title="Roles" subtitle="What members can see and change. Admins always have full access."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/settings/roles/new')}>New role</Button>} />
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Access</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((r) => (
              <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/settings/roles/${r.id}`)}>
                <TableCell sx={{ minWidth: 180 }}>
                  <Typography variant="subtitle2" component="span">{r.name}</Typography>
                  {r.isDefault && <Chip label="default" color="primary" variant="outlined" sx={{ ml: 1 }} />}
                  {r.description && <Typography variant="caption" color="text.secondary" component="div">{r.description}</Typography>}
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{summarize(r.permissions, entityLabelPlural)}</Typography></TableCell>
                <TableCell align="right">{r.memberCount}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {!r.isDefault && (
                    <Tooltip title="Delete">
                      <IconButton onClick={() => setDeleting(r)} aria-label={`Delete ${r.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Members without a role get the default role. Change a member’s role on the Members page.
      </Typography>
      <ConfirmDialog open={!!deleting} title={`Delete “${deleting?.name}”?`}
        message={`${deleting?.memberCount || 0} member(s) will move to the default role.`}
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate(deleting.id)} onClose={() => setDeleting(null)} />
    </>
  )
}

export default RolesPage
